import { useMutation } from "@tanstack/react-query";
import { useDataProvider, useGetIdentity, useRefresh } from "ra-core";
import type { OutreachListKind } from "../types";

/**
 * PRD #16-adjacent — the Offer/Channel split (backlog.md, 2026-09-08).
 * A contact can be active in one list PER AXIS (enforced at the DB level by
 * outreach_list_members_one_active_per_kind, a partial unique index on
 * (contact_id, list_kind)). Setting a new value for an axis soft-removes
 * whatever was active for that same axis first, then inserts the new
 * membership — two writes, not a transaction (no client-side transaction
 * support here), so a failure between them degrades to "no active membership
 * for this axis" rather than a duplicate — acceptable, matches how
 * AddContactToListDialog.tsx already treats this as a best-effort UI
 * operation backed by the DB's own real constraint as the safety net.
 */
export function useSetOutreachMembership(kind: OutreachListKind) {
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const refresh = useRefresh();

  return useMutation({
    mutationFn: async ({
      contactId,
      currentMembershipId,
      newListId,
    }: {
      contactId: number;
      currentMembershipId?: string | number | null;
      newListId: string | number | null;
    }) => {
      if (currentMembershipId) {
        await dataProvider.update("outreach_list_members", {
          id: currentMembershipId,
          data: {
            removed_at: new Date().toISOString(),
            removed_by: identity?.fullName ?? "manual",
          },
          previousData: { id: currentMembershipId },
        });
      }
      if (newListId) {
        await dataProvider.create("outreach_list_members", {
          data: {
            list_id: newListId,
            contact_id: contactId,
            added_by: identity?.fullName ?? "manual",
            list_kind: kind,
          },
        });
      }
    },
    onSuccess: () => refresh(),
  });
}
